import { AxiosResponse } from "axios";
import axios from "../axios";

import { Certificate, UserInfo } from "../../types";
import { entityGenderEnum, entityRoleEnum, entityVerificationStatusEnum } from "../../enums/entity.enum";
import {
  fetchUserFollowersRequestParamsFormatter,
  fetchUserFollowingRequestParamsFormatter,
  getComparedUsersListRequestParamsFormatter,
  getFriendsRequestParamsFormatter,
  getUsersForExploreGetStarsRequestParamsFormatter,
  getUsersForGroupRequestParamsFormatter,
  searchGroupsRequestParamsFormatter,
  searchUsersRequestParamsFormatter,
} from "../formatters/usersFormatter";

type ApiResponse<T> = {
  data: T;
};

type EndorsedSkill = {
  id: number;
  title: string;
  count: string;
  percentage: number | null;
};

type SkillsResponse = {
  data: EndorsedSkill[];
  count: number;
};

export const fetchEntityProfileInfo = async () => {
  try {
    const response = await axios.get(`/users/profile/v1`);
    localStorage.setItem("entityId", response?.data?.data?.entity?.id);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch profile data");
  }
};

export const getUserDetails = async (userId: string): Promise<UserInfo> => {
  try {
    const response: AxiosResponse<ApiResponse<UserInfo>> = await axios.get(`users/${userId}/v1`);

    return response.data.data;
  } catch (err) {
    throw new Error(`Failed to fetch entity details: ${(err as Error).message}`);
  }
};

export const getUserCertificates = async (userId: string): Promise<Certificate[]> => {
  try {
    const response: AxiosResponse<ApiResponse<Certificate[]>> = await axios.get(`users/${userId}/certificates/v1`);
    return response.data.data;
  } catch (err) {
    throw new Error(`Failed to fetch entity certificates: ${(err as Error).message}`);
  }
};

export const getUserSkills = async (userId: string): Promise<SkillsResponse> => {
  try {
    const response: AxiosResponse<SkillsResponse> = await axios.get(`users/${userId}/skills/v1`);
    return response.data;
  } catch (err) {
    throw new Error(`Failed to fetch entity skills: ${(err as Error).message}`);
  }
};

export const followUser = async (userId: string) => {
  try {
    const response = await axios.patch(`users/${userId}/follow`);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to follow user");
  }
};

export const unFollowUser = async (userId: string) => {
  try {
    const response = await axios.delete(`users/${userId}/follow`);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to follow user");
  }
};

export const compareUsers = async (compareIds: string) => {
  try {
    const response = await axios.get(`/users?userIds=${compareIds}`);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch compare users result");
  }
};

export const fetchUserFollowers = async (
  userId: number,
  limit: number,
  offset: number,
  location?: string | null | "",
  country?: string | null | "",
  gender?: entityGenderEnum | string | null | "",
  verificationStatus?: entityVerificationStatusEnum | string | null | "",
  entityRole?: entityRoleEnum | string | null | "",
  nationalityId?: number | null | "",
  filters?: Record<string, any>
) => {
  try {
    const params = fetchUserFollowersRequestParamsFormatter(
      limit,
      offset,
      location,
      country,
      gender,
      verificationStatus,
      entityRole,
      nationalityId,
      filters
    );

    const response = await axios.get(`/users/${userId}/followers${params}`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch user followers");
  }
};

export const getFriends = async (
  userId: number,
  limit: number = 10,
  offset: number = 0,
  location?: string | "" | null,
  country?: string | "" | null,
  gender?: entityGenderEnum | "" | string | null,
  verificationStatus?: entityVerificationStatusEnum | "" | string | null,
  entityRole?: entityRoleEnum | null | string | "",
  nationalityId?: number | null,
  filters?: Record<string, any>
) => {
  const params = getFriendsRequestParamsFormatter(
    limit,
    offset,
    location,
    country,
    gender,
    verificationStatus,
    entityRole,
    nationalityId,
    filters
  );

  try {
    const response = await axios.get(`users/${userId}/network/v1${params}`);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch user friends");
  }
};

export const searchUsers = async (
  limit: number,
  offset: number,
  searchValue?: string,
  countryId?: string | "",
  nationalityId?: string | "",
  gender?: entityGenderEnum | string | "",
  verificationStatus?: "VERIFIED" | "",
  role?: string | "",
  trending?: boolean,
  isFollowing?: boolean
) => {
  try {
    const params = searchUsersRequestParamsFormatter(
      limit,
      offset,
      searchValue,
      countryId,
      nationalityId,
      gender,
      verificationStatus,
      role,
      trending,
      isFollowing
    );

    const response = await axios.get(`/search/v1/users${params}`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch searched users");
  }
};

export const searchGroups = async (
  limit: number,
  offset: number,
  searchValue?: string,
  countryId?: string | "",
  nationalityId?: string | "",
  gender?: entityGenderEnum | "",
  verificationStatus?: "VERIFIED" | "",
  role?: string | "",
  trending?: boolean,
  isFollowing?: boolean
) => {
  try {
    const params = searchGroupsRequestParamsFormatter(
      limit,
      offset,
      searchValue,
      countryId,
      nationalityId,
      gender,
      verificationStatus,
      role,
      trending,
      isFollowing
    );
    const response = await axios.get(`/search/v1/users${params}`);

    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch user groups");
  }
};

export const getTrendingTalents = async () => {
  try {
    const response = await searchUsers(3, 0, "", "", "", "", "", "PLAYER", true);
    return response.items;
  } catch (err) {
    throw new Error(err + " Failed to get trending talents");
  }
};

export const getTrendingProfessionals = async () => {
  try {
    const response = await searchUsers(3, 0, "", "", "", "", "", "COACH,AGENT,SCOUT,OTHER", true);
    return response.items;
  } catch (err) {
    throw new Error(err + " Failed to get trending professionals");
  }
};

export const visitUser = async (userId: string): Promise<any> => {
  try {
    const response = await axios.post<any>(`/users/${userId}/visited`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed visit user profile");
  }
};

export const getUsersForExploreGetStars = async (offset: number) => {
  try {
    const params = getUsersForExploreGetStarsRequestParamsFormatter(offset);
    const response = await axios.get(`/search/v1/users${params}`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch searched users");
  }
};

export const fetchUserFollowing = async (
  userId: number,
  limit: number,
  offset: number,
  location?: string | null,
  country?: string | null,
  gender?: entityGenderEnum | string | null,
  verificationStatus?: entityVerificationStatusEnum | string | null,
  entityRole?: entityRoleEnum | string | null,
  nationalityId?: number | null,
  filters?: Record<string, any>
) => {
  try {
    const queryParams = fetchUserFollowingRequestParamsFormatter({
      limit,
      offset,
      location,
      country,
      gender,
      verificationStatus,
      entityRole,
      nationalityId,
      filters,
    });

    const response = await axios.get(`/users/${userId}/following${queryParams}`);

    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch network followers");
  }
};

export const getUsersForGroups = async (offset: number, searchText: string) => {
  try {
    const params = getUsersForGroupRequestParamsFormatter(offset, searchText);
    const response = await axios.get(`/search/v1/users${params}`);
    return response.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch searched users");
  }
};

export const getComparedUsersList = async (compareIds: string) => {
  try {
    const params = getComparedUsersListRequestParamsFormatter(compareIds);

    const response = await axios.get(`/users${params}`);
    return response.data.data;
  } catch (err) {
    throw new Error(err + " Failed to fetch compare network result");
  }
};

export const getMediaURL = async (fileName: string) => {
  try {
    const response = await axios.post("/media-persigned-url", { files: [{ name: fileName }] });

    return response.data.data[0].url;
  } catch (err) {
    throw new Error(err + " Failed to get media url");
  }
};

export const uploadFileToS3 = async (file?: any, presignedUrl?: string) => {
  if (!file || !presignedUrl) return;
  const response = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });

  if (!response.ok) throw new Error("File upload failed");

  return presignedUrl.split("?")[0];
};
